import React, {useState} from 'react'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import OutsideClickHandler from 'react-outside-click-handler'

import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'

import {
  TriggerElement,
  PopupWrapper,
  ListHeader,
  ListElement,
  ListLinkElement,
  ListItemElement,
  NameElement
} from '../commonStyles'

import {CurrencyType} from '@daedalus/core/src/localization/types/CurrencyType'

const popularCurrenciesWrapperStyles = ({theme}) => css`
  background: ${theme.colors.preferencePopupPopularBg};
  padding: ${theme.layout.spacing.s300}px;
  margin-top: -${theme.layout.spacing.s300}px;
  margin-left: -${theme.layout.spacing.s300}px;

  a {
    padding: ${theme.layout.spacing.s300}px;
  }
`

const PopularCurrenciesWrapper = styled('div')(popularCurrenciesWrapperStyles)

const currencyListLinkStyles = ({theme}) => css`
  padding: ${theme.layout.spacing.s300}px;
  align-items: end;
`

const CurrencyListLinkElement = styled(ListLinkElement)(currencyListLinkStyles)

const currencySymbolStyles = ({theme}) => css`
  color: ${theme.colors.preferencePopupSymbol};
  display: inline-block;
  min-width: 35px;
  margin-right: ${theme.layout.spacing.s200}px;
  min-width: ${theme.layout.spacing.s700}px;
  font-family: ${theme.fonts.family.default};
  font-size: ${theme.fonts.size.sm};
  line-height: ${theme.fonts.lineHeight.md};
  font-weight: ${theme.fonts.weight.md};
  overflow: hidden;
`

const CurrencySymbolElement = styled('span')(currencySymbolStyles)

const triggerCurrencySymbolStyles = ({theme}) => css`
  color: ${theme.colors.preferenceSelectorSymbol};
  width: auto;
  margin: 0;
  font-size: ${theme.fonts.size.md};
  font-weight: bold;
`

const TriggerCurrencySymbolElement = styled(CurrencySymbolElement)(
  triggerCurrencySymbolStyles
)

type Props = {
  /** List of currencies to be displayed */
  currenciesList: CurrencyType[]
  /** The currently selected value */
  currentCurrency: string
  /** The callback for when a currency is selected. The new currency code will be passed. */
  onChangeCurrency: (code: string) => void
  /** List of currencies to be highlighted and shown first. */
  popularCurrenciesList: CurrencyType[]
  /** The callback when the button is clicked */
  onClick?: () => void
}

interface Item extends Pick<Props, 'currentCurrency'> {
  currencyObj: CurrencyType
  handleOnChangeCurrency: (e: React.MouseEvent, code: string) => void
}

const Item = ({currencyObj, currentCurrency, handleOnChangeCurrency}: Item) => {
  if (!currencyObj) {
    return null
  }

  return (
    <ListItemElement key={currencyObj.code}>
      <CurrencyListLinkElement
        href="#"
        isActive={currentCurrency === currencyObj.code}
        onClick={e => handleOnChangeCurrency(e, currencyObj.code)}
      >
        <CurrencySymbolElement>{currencyObj.symbol}</CurrencySymbolElement>
        <NameElement>{currencyObj.displayName}</NameElement>
      </CurrencyListLinkElement>
    </ListItemElement>
  )
}

interface List
  extends Pick<
    Props,
    'currenciesList' | 'popularCurrenciesList' | 'currentCurrency'
  > {
  handleOnChangeCurrency: (e: React.MouseEvent, code: string) => void
}

const List = ({
  currenciesList,
  popularCurrenciesList,
  currentCurrency,
  handleOnChangeCurrency
}: List) => {
  return (
    <ListElement>
      {popularCurrenciesList.length > 0 && (
        <PopularCurrenciesWrapper>
          {popularCurrenciesList.map(currencyObj => (
            <Item
              key={currencyObj.code}
              currencyObj={currencyObj}
              currentCurrency={currentCurrency}
              handleOnChangeCurrency={handleOnChangeCurrency}
            />
          ))}
        </PopularCurrenciesWrapper>
      )}
      {currenciesList.map(currencyObj => (
        <Item
          key={currencyObj.code}
          currencyObj={currencyObj}
          currentCurrency={currentCurrency}
          handleOnChangeCurrency={handleOnChangeCurrency}
        />
      ))}
    </ListElement>
  )
}

const DesktopCurrencySelector = ({
  popularCurrenciesList = [],
  currenciesList,
  onChangeCurrency,
  currentCurrency,
  onClick
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)

  const togglePopup = () => setIsOpen(!isOpen)

  const handleOutsideClick = () => togglePopup()

  const handleClick = () => {
    if (onClick) onClick()
    togglePopup()
  }

  const handleOnChangeCurrency = (e: React.MouseEvent, currency: string) => {
    e.stopPropagation()
    e.preventDefault()
    togglePopup()

    onChangeCurrency(currency)
  }

  const currentCurrencyObject = [
    ...popularCurrenciesList,
    ...currenciesList
  ].find(currency => currency.code === currentCurrency)

  return (
    <TriggerElement onClick={handleClick}>
      <TriggerCurrencySymbolElement data-id="CurrencySelectorTrigger">
        {currentCurrencyObject?.symbol || currentCurrency}
      </TriggerCurrencySymbolElement>

      {isOpen && (
        <OutsideClickHandler onOutsideClick={handleOutsideClick}>
          <PopupWrapper>
            <ListHeader>
              <FormattedMessageWrapper
                dataId="SelectCurrency"
                id="selectCurrency"
                defaultMessage="Select currency"
              />
            </ListHeader>
            <List
              handleOnChangeCurrency={handleOnChangeCurrency}
              currenciesList={currenciesList}
              popularCurrenciesList={popularCurrenciesList}
              currentCurrency={currentCurrency}
            />
          </PopupWrapper>
        </OutsideClickHandler>
      )}
    </TriggerElement>
  )
}

export default DesktopCurrencySelector
